import { guardNumber } from "@msuite/utils/numbers";
import clsx from "clsx";
import { Fragment, type HTMLAttributes, type Ref, useState } from "react";
import { Group } from "react-aria-components";
import {
	type Control,
	Controller,
	type FieldValues,
	type Path,
	type PathValue,
} from "react-hook-form";
import RenderHooks from "render-hooks";
import { Button } from "../button";
import { useDebouncedEffect } from "../hooks";
import { Icon, type IconType, icon } from "../icons";
import { Spinner } from "../spinner";
import { cn } from "../utils";
import { type ExtendedInputWrapperProps, InputWrapper } from "./input-wrapper";

export interface TextInputProps<T extends FieldValues>
	extends Omit<ExtendedInputWrapperProps<T>, "name">,
		HTMLAttributes<HTMLInputElement> {
	type?:
		| "text"
		| "password"
		| "email"
		| "number"
		| "tel"
		| "url"
		| "search"
		| "pas"
		| "datetime-local"
		| "date"
		| "time";
	ref?: Ref<HTMLInputElement>;
	placeholder?: string;
	value?: string | null;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
	className?: string;
	leftIcon?: IconType;
	isDisabled?: boolean;
	control: Control<T>;
	name: Path<T>;
	defaultValue?: PathValue<T, Path<T>>;
	min?: number;
	max?: number;
	step?: number;
	inputMiddleware?: (value: string) => string;
	outputMiddleware?: (value: string) => string;
	allowOnIconClick?: boolean;
	isLoading?: boolean;
	shouldUnregister?: boolean;
	/**
	 * Delay (ms) before a keystroke is committed to the form. Keeps typing snappy
	 * when the field lives in a large form/field-array, where committing on every
	 * keystroke would otherwise re-render the whole tree. Omit for the default,
	 * uncommitted-on-every-keystroke behavior.
	 */
	debounceMs?: number;
}

export function TextInput<T extends FieldValues>({
	type = "text",
	placeholder,
	value,
	onChange,
	className,
	autoFocus,
	onBlur,
	onFocus,
	step,

	/**
	 * InputWrapper
	 */
	control,
	name,
	label,
	helperText,
	isRequired,
	defaultValue,

	inputMiddleware,
	outputMiddleware,

	/**
	 * Icons
	 */
	leftIcon,

	/** States */
	isDisabled,

	/** Refs */
	ref,
	shouldUnregister,

	allowOnIconClick,
	isLoading = false,

	debounceMs,

	...rest
}: TextInputProps<T>) {
	/** State */
	const [forceTextType, setForceTextType] = useState(false);

	/** Functions */
	function onToggleForceTextType() {
		setForceTextType(!forceTextType);
	}

	/** Computed */
	const isSearchType = type === "search";
	const isPasswordType = type === "password";
	const hasLeftIcon = Boolean(leftIcon) || type === "search";
	const LeftIcon = isSearchType ? icon("Search") : leftIcon || (() => null);

	/** Render */
	return (
		<Controller
			control={control}
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			render={({ field, formState }) => (
				<InputWrapper
					label={label}
					name={field.name}
					helperText={helperText}
					isRequired={isRequired}
					errors={formState.errors}
				>
					<Group className="relative">
						<RenderHooks hooks={{ useDebouncedEffect } as const}>
							{({ useDebouncedEffect, useState, useEffect }) => {
								const [localValue, setLocalValue] = useState<unknown>(field.value);

								useEffect(() => {
									setLocalValue(field.value);
								}, [field.value]);

								useDebouncedEffect(
									() => {
										if (!debounceMs || localValue === field.value) return;
										field.onChange(localValue);
									},
									[localValue],
									debounceMs ?? 0,
								);

								const displayValue = (debounceMs ? localValue : field.value) as
									| string
									| number
									| undefined;

								function commitValue(nextValue: unknown) {
									if (debounceMs) {
										setLocalValue(nextValue);
									} else {
										field.onChange(nextValue);
									}
								}

								return (
									<input
										autoComplete="off"
										className={clsx(
											"input",
											hasLeftIcon && "pl-10",
											formState.errors[field.name] && "input-error",
											className,
										)}
										id={field.name}
										ref={ref}
										type={forceTextType ? "text" : type}
										placeholder={placeholder}
										value={
											inputMiddleware
												? inputMiddleware(String(displayValue ?? ""))
												: (displayValue ?? undefined)
										}
										step={step}
										onWheel={handleOnWheel}
										onChange={(event) => {
											if (type === "number") {
												commitValue(event.target.value === "" ? "" : +event.target.value);
											} else if (outputMiddleware) {
												commitValue(outputMiddleware(event.target.value));
											} else {
												commitValue(event.target.value);
											}
										}}
										name={field.name}
										autoFocus={autoFocus}
										onBlur={onBlur}
										onFocus={onFocus}
										disabled={isDisabled}
										{...rest}
									/>
								);
							}}
						</RenderHooks>
						{hasLeftIcon && (
							<div
								className={clsx(
									"top-0 left-0 absolute flex justify-center items-center w-10 h-full",
									!allowOnIconClick && "pointer-events-none",
									isSearchType &&
										guardNumber((field.value as string)?.length) > 0 &&
										"*:stroke-accent-500!",
								)}
							>
								<LeftIcon className="stroke-primary-400 dark:stroke-white" />
							</div>
						)}
						{isLoading && (
							<div
								className={clsx(
									"top-0 absolute flex justify-center items-center w-10 h-full",
									isSearchType ? "right-8" : "right-0",
									!allowOnIconClick && "pointer-events-none",
								)}
							>
								<Spinner size={15} />
							</div>
						)}
						{isPasswordType && (
							<div className="top-0 right-1.5 absolute flex justify-center items-center h-full">
								<Button
									icon={forceTextType ? <Icon name="Eye" /> : <Icon name="EyeOff" />}
									variant="ghost"
									onClick={onToggleForceTextType}
								/>
							</div>
						)}
						{type === "number" && (
							<Fragment>
								{(["Up", "Down"] as const).map((direction) => (
									<button
										key={direction}
										type="button"
										className={cn(
											"absolute right-0 h-1/2  border-l px-2 border-color",
											"cursor-pointer",
											direction === "Up" ? "top-0 border-b" : "bottom-0",
										)}
										onClick={() => {
											const currentValue = Number(field.value);
											const newValue =
												direction === "Up"
													? currentValue + (step || 1)
													: currentValue - (step || 1);
											field.onChange(newValue);
										}}
									>
										<Icon name={direction === "Up" ? "ChevronUp" : "ChevronDown"} />
									</button>
								))}
							</Fragment>
						)}
					</Group>
				</InputWrapper>
			)}
		/>
	);
}

function handleOnWheel(event: React.WheelEvent<HTMLInputElement>) {
	event.currentTarget.blur();
}
